/**
 * @file use-product-form-state.ts
 *
 * Controlled-form state for the product form's scalar fields (no react-hook-form).
 * Holds the editable fields, exposes a `patch` updater, tracks whether the form
 * is dirty against its seed, and runs hand-rolled validation. Validation errors
 * surface only after the first `validate()` call (submit) so the form doesn't
 * shout at an untouched field.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { RefObject } from 'react';
import type { ShopProduct } from '../../api/shop-types';
import { adminMessages } from '../../i18n/messages';
import type { ProductFormErrors, ProductFormFields } from './product-form-types';
import {
  deriveFields,
  isInvalidPriceInput,
  isSalePriceVersusRegularInvalid,
  shallowEqual,
} from './product-form-utils';

/** What {@link useProductFormState} returns to the form. */
export interface UseProductFormStateResult {
  fields: ProductFormFields;
  patch: (update: Partial<ProductFormFields>) => void;
  isDirty: boolean;
  errors: ProductFormErrors;
  validate: () => boolean;
  /** Attach to the name field; focused/scrolled to when validation fails. */
  firstErrorRef: RefObject<HTMLInputElement | null>;
}

/**
 * Manages the product form's scalar field state and validation.
 *
 * @param product - The seed product (edit/duplicate), or `undefined` to create.
 * @param duplicate - When `true`, seeds a copy (name “ (Copy)”, cleared SKU).
 *
 * @returns The fields, updater, dirty flag, errors, and a `validate` function.
 */
export function useProductFormState(
  product: ShopProduct | undefined,
  duplicate = false,
): UseProductFormStateResult {
  const intl = useIntl();
  const [fields, setFields] = useState<ProductFormFields>(() =>
    deriveFields(product, duplicate),
  );
  const initialRef = useRef<ProductFormFields>(fields);
  const seededProductRef = useRef(product);
  const [submitted, setSubmitted] = useState(false);
  const firstErrorRef = useRef<HTMLInputElement | null>(null);

  // Re-seed only when the source product reference changes (e.g. it resolves
  // after a fetch); the initial value is already seeded by `useState`.
  useEffect(() => {
    if (seededProductRef.current === product) {
      return;
    }
    seededProductRef.current = product;
    const seeded = deriveFields(product, duplicate);
    setFields(seeded);
    initialRef.current = seeded;
    setSubmitted(false);
  }, [product, duplicate]);

  const patch = useCallback((update: Partial<ProductFormFields>) => {
    setFields((prev) => ({ ...prev, ...update }));
  }, []);

  const computeErrors = useCallback(
    (current: ProductFormFields): ProductFormErrors => {
      const errors: ProductFormErrors = {};
      if (current.name.trim() === '') {
        errors.nameError = intl.formatMessage(adminMessages.formNameRequired);
      }
      if (isInvalidPriceInput(current.regularPrice)) {
        errors.regularPriceError = intl.formatMessage(
          adminMessages.formRegularPriceInvalid,
        );
      }
      if (isInvalidPriceInput(current.salePrice)) {
        errors.salePriceFormatError = intl.formatMessage(
          adminMessages.formSalePriceInvalid,
        );
      } else if (
        isSalePriceVersusRegularInvalid(
          current.regularPrice,
          current.salePrice,
          current.price,
        )
      ) {
        errors.salePriceCheckError = intl.formatMessage(
          adminMessages.formSalePriceTooHigh,
        );
      }
      return errors;
    },
    [intl],
  );

  const errors = useMemo(
    () => (submitted ? computeErrors(fields) : {}),
    [submitted, computeErrors, fields],
  );

  const validate = useCallback((): boolean => {
    setSubmitted(true);
    const result = computeErrors(fields);
    const valid = Object.keys(result).length === 0;
    if (!valid && result.nameError) {
      firstErrorRef.current?.focus?.();
      firstErrorRef.current?.scrollIntoView?.({ block: 'center' });
    }
    return valid;
  }, [computeErrors, fields]);

  const isDirty = useMemo(
    () => !shallowEqual(fields, initialRef.current),
    [fields],
  );

  return { fields, patch, isDirty, errors, validate, firstErrorRef };
}
